import React, { MouseEvent, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { RoomListType, RoomStatusType, RoomType } from '@/models/room-manager/response/guest-room-indicator'
import { IndicatorRoom } from '@/components/molecules/room-manager/indicator-room'
import { HEADER_HEIGHT } from '@/constants/layout'
import { EmptyRoomPopup } from '../room-popup/empty-room-popup'
import { StayPopup } from '../room-popup/stay-popup'
import { CheckInPopup } from '../room-popup/check-in-popup'
import { CheckOutPopup } from '../room-popup/check-out-popup'
import { CleaningPopup } from '../room-popup/cleaning-popup'
import { PopupType } from '../room-popup/popup-type'
import { RoomManagerIndicatorContext, RoomStatusFilter, RoomTypeFilter } from '@/components/pages/room-manager/indicator'
import { useTranslation } from 'react-i18next'
import uuid from 'react-uuid'
import { useHoverGroupingForIndicator } from '@/hooks/use-hover-grouping-for-indicator'

type Props = {
  roomStatusFilter: RoomStatusFilter | undefined
  roomTypeFilter: RoomTypeFilter | undefined
  roomEachFloor: RoomListType[]
}

export const DefaultRoomBoxSize = {
  width: 83,
  height: 60,
}

/**
 * フロアごとの部屋一覧を表示するコンポーネント
 * @param roomStatusFilter - 部屋ステータスフィルター
 * @param roomTypeFilter - 部屋タイプフィルター
 * @param roomEachFloor - フロアごとの部屋リスト
 */
export const AllRoomsOnFloor: React.FC<Props> = ({ roomStatusFilter, roomTypeFilter, roomEachFloor }) => {
  const [selectedRoom, setSelectedRoom] = useState<RoomType>()
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [popup, setPopup] = useState<PopupType>()
  const roomAreaRef = useRef<HTMLDivElement>(null)
  const { reloadEvent, columnsPerRow } = useContext(RoomManagerIndicatorContext)
  const { t, i18n } = useTranslation()
  const [boxZoomRate, setBoxZoomRate] = useState<number>(1)
  const [selectRoomRect, setSelectRoomRect] = useState<DOMRect>()
  const FLOOR_BOX_COUNT = 1
  const roomColumns = columnsPerRow ?? 15
  const numberOfBoxesPerRow = roomColumns + FLOOR_BOX_COUNT

  // カスタムフック: 部屋のハイライト管理
  const { getShowReservation, isHighlighted, handleMouseEnter, handleMouseLeave } = useHoverGroupingForIndicator(roomEachFloor)

  useEffect(() => {
    initializeComponent()
  }, [reloadEvent])

  useEffect(() => {
    const roomAreaRect = roomAreaRef?.current?.getBoundingClientRect()
    if (!roomAreaRect) {
      return
    }
    const boxSizeRate = roomAreaRect.width / numberOfBoxesPerRow / DefaultRoomBoxSize.width
    setBoxZoomRate(boxSizeRate)
  }, [roomAreaRef, numberOfBoxesPerRow])

  /**
   * コンポーネントを初期化（ポップアップと選択状態をリセット）
   */
  const initializeComponent = () => {
    setPopup(undefined)
    setSelectedRoom(undefined)
  }

  /**
   * ポップアップが表示領域を超えないようにトップ位置を調整
   * @param top - ポップアップの上端位置
   * @param bottom - ポップアップの下端位置
   * @returns 調整後のトップ位置
   */
  const adjustTopNotExceedArea = (top: number, bottom: number): number => {
    const roomAreaRect = roomAreaRef.current?.getBoundingClientRect()
    const areaTop = roomAreaRect?.top || 0
    const areaBottom = (roomAreaRect?.bottom || window.outerHeight) + window.scrollY

    let exceedSize = 0
    if (areaTop > top) {
      exceedSize = top - areaTop
    } else if (areaBottom < bottom) {
      exceedSize = bottom - areaBottom
    }
    return top - exceedSize
  }

  /**
   * 部屋を選択してポップアップを表示
   * @param room - 選択された部屋（undefinedの場合は選択解除）
   * @param e - クリックイベント
   */
  const onSelectRoom = (room: RoomType | undefined, e?: MouseEvent<HTMLDivElement>) => {
    setSelectedRoom(room)

    if (!e || !room) {
      setPopup(undefined)
      return
    }

    const popup = computedPopupComponent(room)
    setPopup(popup)
    setSelectRoomRect(e.currentTarget.getBoundingClientRect())
  }

  /**
   * 部屋のステータスに応じたポップアップコンポーネントを取得
   * @param room - 部屋情報
   * @returns ポップアップタイプ
   */
  const computedPopupComponent = (room: RoomType): PopupType => {
    switch (room.roomStatus) {
      case RoomStatusType.empty:
        return new EmptyRoomPopup()
      case RoomStatusType.checkIn:
        return new CheckInPopup(room)
      case RoomStatusType.stay:
        return new StayPopup(room)
      case RoomStatusType.checkOut:
        return new CheckOutPopup(room)
      case RoomStatusType.cleaning:
        return new CleaningPopup(room)
    }
  }

  /**
   * フィルター条件に一致しない部屋を無効化するか判定
   * @param room - 部屋情報
   * @returns 無効化する場合true
   */
  const computedDisable = (room: RoomType): boolean => {
    const isNotSelectedRoomType = !!(roomTypeFilter?.length && !roomTypeFilter.includes(room.roomTypeId))
    const isNotSelectedRoomStatus = !!(roomStatusFilter?.length && !roomStatusFilter.includes(room.roomStatus))
    return isNotSelectedRoomType || isNotSelectedRoomStatus
  }

  /**
   * フィルターが適用されているか判定
   * @returns フィルター適用中の場合true
   */
  const isFiltering = (): boolean => {
    return !!(roomTypeFilter?.length || roomStatusFilter?.length)
  }

  /**
   * 各フロアの部屋リストに空白を追加して表示用に整形
   * 折り返し後の行の残り部分や画面下部の空白を埋める
   */
  const filledRoomEachFloor = useMemo(() => {
    const roomAreaRect = roomAreaRef.current?.getBoundingClientRect()
    if (!roomAreaRect) {
      return roomEachFloor
    }
    const minAreaHeight = window.innerHeight - roomAreaRect.top
    const maxFloorCount = Math.floor(minAreaHeight / (DefaultRoomBoxSize.height * boxZoomRate))
    const maxRowRoomCount = numberOfBoxesPerRow - 1 // -1 = floor box num

    // 折り返し表示にするため、各フロアの最後の行の残り部分を空白で埋める
    const filledFloor = roomEachFloor.map(floor => {
      const blankNum = floor.rooms.length % maxRowRoomCount
      if (blankNum === 0) {
        return floor
      }
      return {
        ...floor,
        rooms: [...floor.rooms, ...[...Array(maxRowRoomCount - blankNum)].map(() => undefined)],
      }
    })

    // 各フロアの実際の行数を計算（1フロアが複数行に折れる場合を考慮）
    const actualFloorRowCount = roomEachFloor.reduce((sum, floor) => {
      return sum + Math.ceil(floor.rooms.length / maxRowRoomCount)
    }, 0)
    const filledFloorCount = maxFloorCount - actualFloorRowCount
    if (filledFloorCount <= 0) {
      return filledFloor
    }

    const blankFloor = [...Array(filledFloorCount)].map(
      (): RoomListType => ({
        floorId: uuid(),
        floorName: '',
        floorNameEn: '',
        rooms: [...Array(maxRowRoomCount)].map(() => undefined),
      }),
    )
    return [...filledFloor, ...blankFloor]
  }, [roomEachFloor, boxZoomRate, numberOfBoxesPerRow, reloadEvent])

  useLayoutEffect(() => {
    if (popup && selectRoomRect) {
      const popupHeight = popup.getHeight ? popup.getHeight() : popup.height
      const popupWidth = popup.getWidth ? popup.getWidth() : popup.width

      // トップ再計算
      const computedPopupTop = selectRoomRect.top - (popupHeight - selectRoomRect.height) / 2 + window.scrollY

      const topPosition = adjustTopNotExceedArea(computedPopupTop, computedPopupTop + popupHeight) - HEADER_HEIGHT

      // レフト再計算
      const areaRect = roomAreaRef.current?.getBoundingClientRect()
      const areaAbsoluteRight = (areaRect?.right || window.innerWidth) + window.scrollX
      const areaAbsoluteLeft = (areaRect?.left || 0) + window.scrollX
      const absoluteLeft = selectRoomRect.left + window.scrollX
      const absoluteRight = selectRoomRect.right + window.scrollX

      const wouldOverflowRight = absoluteRight + popupWidth > areaAbsoluteRight
      const wouldOverflowLeft = absoluteLeft - popupWidth < areaAbsoluteLeft

      const left = wouldOverflowRight && !wouldOverflowLeft ? absoluteLeft - popupWidth - 8 : absoluteRight + 8

      setPopupPosition({ top: topPosition, left })
    }
  }, [popup, selectRoomRect])

  return (
    <div ref={roomAreaRef} css={roomAreaContainerStyle}>
      {filledRoomEachFloor.map(roomList => (
        <div key={roomList.floorId} css={css({ display: 'flex' })}>
          <div
            css={[
              baseBoxStyle,
              floorStyle,
              {
                zoom: boxZoomRate,
              },
            ]}
          >
            <p>{i18n.language === 'ja' ? roomList.floorName : roomList.floorNameEn}</p>
          </div>
          <div css={roomRowStyle}>
            {roomList.rooms.map(room =>
              room ? (
                (() => {
                  const reservation = getShowReservation(room)

                  return (
                    <IndicatorRoom
                      reservation={reservation}
                      key={room.roomId}
                      room={room}
                      isSelected={selectedRoom?.roomId === room.roomId}
                      isDisable={computedDisable(room)}
                      onClick={e => onSelectRoom(room, e)}
                      boxZoomRate={boxZoomRate}
                      isHighlighted={isHighlighted(reservation)}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  )
                })()
              ) : (
                <div
                  key={uuid()}
                  css={[
                    baseBoxStyle,
                    {
                      zoom: boxZoomRate,
                    },
                  ]}
                >
                  <div css={roomStyle}></div>
                  {isFiltering() && <div css={disableStyle} />}
                </div>
              ),
            )}
          </div>
        </div>
      ))}
      {popup && selectedRoom && (
        <>
          <popup.Component position={popupPosition} room={selectedRoom} t={t} />
          <div css={popupBackgroundStyle} onClick={() => onSelectRoom(undefined)}></div>
        </>
      )}
    </div>
  )
}

const roomAreaContainerStyle = css({
  height: '100%',
  overflowX: 'hidden',
  overflowY: 'auto',
})

const baseBoxStyle = css({
  margin: 1,
  position: 'relative',
  minWidth: DefaultRoomBoxSize.width - 2,
  width: DefaultRoomBoxSize.width - 2,
  minHeight: DefaultRoomBoxSize.height - 2,
  height: DefaultRoomBoxSize.height - 2,
})

const roomRowStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
})

const floorStyle = css({
  backgroundColor: '#F5F5F5',
  padding: '0 14px',
  height: 'auto',
  display: 'flex',
  justifyContent: 'center',
  flexFlow: 'column',
  p: {
    color: '#272727',
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.6,
    lineHeight: 1.5,
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': '2',
    overflow: 'hidden',
  },
})

const popupBackgroundStyle = css({
  height: '100%',
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
})
const roomStyle = css({
  height: '100%',
  width: '100%',
  backgroundColor: '#F5F5F5',
  position: 'absolute',
})

const disableStyle = css({
  width: 'calc(100% + 2px)',
  height: 'calc(100% + 2px)',
  background: '#000000',
  opacity: 0.3,
  position: 'absolute',
})
